import m from 'mithril'
import { css } from '@emotion/css'
import { authModel } from '../models/auth.js'
import {
  colors, font, space, card, btnPrimary, btnSecondary,
  input as inputStyle, label as labelStyle,
} from '../styles/theme.js'
import { projects as projectsApi, members as membersApi, projectInvites as projectInvitesApi, predictions as predictionsApi, scenarioPacks as scenarioApi } from '../lib/api.js'

// --- Styles ---

const breadcrumb = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-bottom: ${space.lg};
  a { color: ${colors.textSecondary}; }
`

const pageTitle = css`
  font-size: ${font.size['2xl']};
  font-weight: ${font.weight.bold};
  margin-bottom: ${space.xl};
`

const section = css`
  margin-bottom: ${space.xl};
`

const sectionTitle = css`
  font-size: ${font.size.lg};
  font-weight: ${font.weight.semibold};
  margin-bottom: ${space.md};
`

const memberRow = css`
  display: flex;
  align-items: center;
  gap: ${space.md};
  padding: ${space.sm} ${space.md};
  border-bottom: 1px solid ${colors.border};
  &:last-child { border-bottom: none; }
`

const memberInfo = css`
  flex: 1;
  min-width: 0;
`

const memberName = css`
  font-weight: ${font.weight.medium};
`

const memberEmail = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
`

const roleBadge = css`
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`

const roleSelect = css`
  font-size: ${font.size.sm};
  padding: 3px ${space.sm};
  border: 1px solid ${colors.border};
  border-radius: 4px;
  background: ${colors.bgCard};
  cursor: pointer;
`

const removeBtn = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  background: none;
  border: 1px solid ${colors.border};
  border-radius: 4px;
  padding: 3px 8px;
  cursor: pointer;
  &:hover { color: ${colors.brand}; border-color: ${colors.brand}; }
`

const inviteForm = css`
  display: flex;
  gap: ${space.sm};
  align-items: flex-end;
  margin-top: ${space.md};
`

const inviteInput = css`
  ${inputStyle};
  flex: 1;
`

const inviteSelect = css`
  ${inputStyle};
  width: 120px;
`

const inviteBtn = css`
  ${btnPrimary};
  white-space: nowrap;
`

const errMsg = css`
  font-size: ${font.size.sm};
  color: ${colors.brand};
  margin-top: ${space.xs};
`

const successMsg = css`
  font-size: ${font.size.sm};
  color: ${colors.green};
  margin-top: ${space.xs};
`

const youBadge = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  font-style: italic;
`

function roleColor(role) {
  if (role === 'owner') return colors.brand
  if (role === 'editor') return colors.teal
  return colors.textMuted
}

const inviteLinkRow = css`
  display: flex;
  align-items: center;
  gap: ${space.md};
  padding: ${space.sm} ${space.md};
  border-bottom: 1px solid ${colors.border};
  &:last-child { border-bottom: none; }
`

const inviteLinkInfo = css`
  flex: 1;
  min-width: 0;
`

const inviteLinkLabel = css`
  font-weight: ${font.weight.medium};
  font-size: ${font.size.sm};
`

const inviteLinkCode = css`
  font-family: monospace;
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
`

const copyBtn = css`
  font-size: ${font.size.xs};
  color: ${colors.teal};
  background: none;
  border: 1px solid ${colors.teal}40;
  border-radius: 4px;
  padding: 3px 8px;
  cursor: pointer;
  white-space: nowrap;
  &:hover { background: ${colors.teal}10; }
`

const revokedStyle = css`
  opacity: 0.5;
`

const dangerZone = css`
  border: 1px solid ${colors.critical};
  border-radius: 8px;
  padding: ${space.lg};
`

const dangerTitle = css`
  font-size: ${font.size.lg};
  font-weight: ${font.weight.semibold};
  color: ${colors.critical};
  margin-bottom: ${space.md};
`

const dangerDesc = css`
  font-size: ${font.size.sm};
  color: ${colors.textSecondary};
  margin-bottom: ${space.md};
  line-height: 1.5;
`

const dangerBtn = css`
  padding: ${space.sm} ${space.md};
  border-radius: 6px;
  font-size: ${font.size.sm};
  font-weight: ${font.weight.medium};
  border: 1px solid ${colors.critical};
  background: transparent;
  color: ${colors.critical};
  cursor: pointer;
  &:hover:not(:disabled) { background: ${colors.critical}; color: white; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`

const confirmOverlay = css`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`

const confirmCard = css`
  ${card};
  width: 420px;
  padding: ${space.xl};
`

const configForm = css`
  display: flex;
  flex-direction: column;
  gap: ${space.md};
`

const configRow = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
`

const configLabel = css`
  font-size: ${font.size.sm};
  color: ${colors.textSecondary};
  min-width: 100px;
  flex-shrink: 0;
`

const configInput = css`
  ${inputStyle};
  max-width: 300px;
`

const promptTextarea = css`
  ${inputStyle};
  min-height: 120px;
  resize: vertical;
  font-family: monospace;
  font-size: ${font.size.sm};
  width: 100%;
`

const helpText = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  margin-top: 2px;
`

// --- Component ---

export function SettingsView() {
  let project = null
  let loading = true
  let isOwner = false
  let currentUserId = null

  // Invite state
  let inviteEmail = ''
  let inviteRole = 'editor'
  let inviteError = null
  let inviteSuccess = null
  let inviting = false

  // Invite links state
  let inviteLinks = []
  let inviteLinksLoading = false
  let newLinkRole = 'editor'
  let newLinkLabel = ''
  let creatingLink = false
  let copiedCode = null

  // Prediction config state
  let predConfig = null
  let predProvider = 'openai'
  let predModel = 'gpt-4o-mini'
  let predBaseUrl = ''
  let predApiKey = ''
  let predPromptTemplate = ''
  let predGranularity = 'session'
  let predConcurrency = 8
  let predMaxRetries = 3
  let predSaving = false
  let predSaveMsg = null
  let showPredConfig = false

  // Generation config state
  let genConfig = null
  let genProvider = 'openai'
  let genModel = 'gpt-4o'
  let genApiKey = ''
  let genBaseUrl = ''
  let genSaving = false
  let genSaveMsg = null
  let showGenConfig = false

  // Delete state
  let showDeleteConfirm = false
  let deleteConfirmName = ''
  let deleting = false
  let deleteError = null

  async function load(projectId) {
    loading = true
    try {
      project = await projectsApi.get(projectId)
      isOwner = project.memberRole === 'owner'
      currentUserId = authModel.user?.id || null
      if (isOwner) {
        loadInviteLinks(projectId)
        loadPredictionConfig(projectId)
        loadGenConfig(projectId)
      }
    } catch (err) {
      console.error('Failed to load project:', err)
    } finally {
      loading = false
      m.redraw()
    }
  }

  async function loadInviteLinks(projectId) {
    inviteLinksLoading = true
    try {
      inviteLinks = await projectInvitesApi.list(projectId)
    } catch (err) {
      console.error('Failed to load invite links:', err)
    } finally {
      inviteLinksLoading = false
      m.redraw()
    }
  }

  async function createInviteLink(projectId) {
    creatingLink = true
    m.redraw()
    try {
      const link = await projectInvitesApi.create(projectId, {
        role: newLinkRole,
        label: newLinkLabel.trim() || undefined,
      })
      inviteLinks.unshift(link)
      newLinkLabel = ''
    } catch (err) {
      console.error('Failed to create invite link:', err)
    } finally {
      creatingLink = false
      m.redraw()
    }
  }

  async function revokeInviteLink(projectId, inviteId) {
    try {
      await projectInvitesApi.revoke(projectId, inviteId)
      const link = inviteLinks.find(l => l.id === inviteId)
      if (link) link.revoked_at = new Date().toISOString()
    } catch (err) {
      console.error('Failed to revoke invite link:', err)
    }
    m.redraw()
  }

  function copyInviteLink(code) {
    const url = `${window.location.origin}/#!/join/${code}`
    navigator.clipboard.writeText(url)
    copiedCode = code
    setTimeout(() => { copiedCode = null; m.redraw() }, 2000)
    m.redraw()
  }

  async function inviteMember(projectId) {
    if (!inviteEmail.trim()) return
    inviting = true
    inviteError = null
    inviteSuccess = null
    try {
      const member = await membersApi.invite(projectId, {
        email: inviteEmail.trim(),
        role: inviteRole,
      })
      project.members.push(member)
      inviteEmail = ''
      inviteSuccess = `Invited ${member.name || member.email}`
    } catch (err) {
      inviteError = err.message || 'Failed to invite'
    } finally {
      inviting = false
      m.redraw()
    }
  }

  async function changeRole(projectId, userId, newRole) {
    try {
      await membersApi.updateRole(projectId, userId, { role: newRole })
      const member = project.members.find(m => m.id === userId)
      if (member) member.role = newRole
    } catch (err) {
      console.error('Failed to change role:', err)
    }
    m.redraw()
  }

  async function removeMember(projectId, userId) {
    try {
      await membersApi.remove(projectId, userId)
      project.members = project.members.filter(m => m.id !== userId)
    } catch (err) {
      console.error('Failed to remove member:', err)
    }
    m.redraw()
  }

  async function loadPredictionConfig(projectId) {
    try {
      predConfig = await predictionsApi.getConfig(projectId)
      if (predConfig && predConfig.configured !== false) {
        predProvider = predConfig.provider || 'openai'
        predModel = predConfig.model || 'gpt-4o-mini'
        predBaseUrl = predConfig.base_url || ''
        predPromptTemplate = predConfig.prompt_template || ''
        predGranularity = predConfig.default_granularity || 'session'
        predConcurrency = predConfig.concurrency ?? 8
        predMaxRetries = predConfig.max_retries ?? 3
      }
    } catch (err) {
      console.error('Failed to load prediction config:', err)
    }
    m.redraw()
  }

  async function savePredictionConfig(projectId) {
    predSaving = true
    predSaveMsg = null
    m.redraw()
    try {
      const data = {
        provider: predProvider,
        model: predModel,
        defaultGranularity: predGranularity,
        concurrency: predConcurrency,
        maxRetries: predMaxRetries,
      }
      if (predBaseUrl) data.baseUrl = predBaseUrl
      if (predApiKey) data.apiKey = predApiKey
      if (predPromptTemplate) data.promptTemplate = predPromptTemplate
      predConfig = await predictionsApi.saveConfig(projectId, data)
      predApiKey = ''
      predSaveMsg = 'Saved'
      setTimeout(() => { predSaveMsg = null; m.redraw() }, 2000)
    } catch (err) {
      predSaveMsg = err.message || 'Failed to save'
    } finally {
      predSaving = false
      m.redraw()
    }
  }

  async function loadGenConfig(projectId) {
    try {
      genConfig = await scenarioApi.getGenerationConfig(projectId)
      if (genConfig) {
        genProvider = genConfig.provider || 'openai'
        genModel = genConfig.model || 'gpt-4o'
        genBaseUrl = genConfig.base_url || ''
      }
    } catch (err) {
      console.error('Failed to load generation config:', err)
    }
    m.redraw()
  }

  async function saveGenConfig(projectId) {
    genSaving = true
    genSaveMsg = null
    m.redraw()
    try {
      const data = {
        provider: genProvider,
        model: genModel,
      }
      if (genBaseUrl) data.baseUrl = genBaseUrl
      if (genApiKey) data.apiKey = genApiKey
      genConfig = await scenarioApi.setGenerationConfig(projectId, data)
      genApiKey = ''
      genSaveMsg = 'Saved'
      setTimeout(() => { genSaveMsg = null; m.redraw() }, 2000)
    } catch (err) {
      genSaveMsg = err.message || 'Failed to save'
    } finally {
      genSaving = false
      m.redraw()
    }
  }

  async function deleteProject(projectId) {
    deleting = true
    deleteError = null
    m.redraw()
    try {
      await projectsApi.delete(projectId)
      m.route.set('/projects')
    } catch (err) {
      console.error('Failed to delete project:', err)
      deleteError = err.message || 'Failed to delete project'
      deleting = false
      m.redraw()
    }
  }

  return {
    oninit(vnode) {
      load(vnode.attrs.projectId)
    },

    view(vnode) {
      const projectId = vnode.attrs.projectId
      if (loading) return m('div', 'Loading...')
      if (!project) return m('div', 'Project not found')

      const memberList = project.members || []

      return m('div', [
        m('div', { class: breadcrumb }, [
          m(m.route.Link, { href: '/projects' }, 'Projects'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}` }, project.name),
          ' / Settings',
        ]),

        m('h1', { class: pageTitle }, 'Settings'),

        // Members section
        m('div', { class: section }, [
          m('h2', { class: sectionTitle }, `Members (${memberList.length})`),
          m('div', { class: card },
            memberList.map(member =>
              m('div', { key: member.id, class: memberRow }, [
                m('div', { class: memberInfo }, [
                  m('div', { class: memberName }, [
                    member.name || member.email,
                    member.id === currentUserId
                      ? m('span', { class: youBadge }, ' (you)')
                      : null,
                  ]),
                  m('div', { class: memberEmail }, member.email),
                ]),

                // Role display/edit
                isOwner && member.id !== currentUserId
                  ? m('select', {
                      class: roleSelect,
                      value: member.role,
                      onchange: (e) => changeRole(projectId, member.id, e.target.value),
                    }, [
                      m('option', { value: 'viewer' }, 'Viewer'),
                      m('option', { value: 'editor' }, 'Editor'),
                      m('option', { value: 'owner' }, 'Owner'),
                    ])
                  : m('span', {
                      class: roleBadge,
                      style: `color: ${roleColor(member.role)}; background: ${roleColor(member.role)}15;`,
                    }, member.role),

                // Remove button (owner only, not self)
                isOwner && member.id !== currentUserId
                  ? m('button', {
                      class: removeBtn,
                      onclick: () => removeMember(projectId, member.id),
                    }, 'Remove')
                  : null,
              ])
            )
          ),

          // Invite form (owner only)
          isOwner
            ? m('div', { class: inviteForm }, [
                m('div', { style: 'flex:1;' }, [
                  m('label', { class: labelStyle }, 'Invite by email'),
                  m('input', {
                    class: inviteInput,
                    type: 'email',
                    placeholder: 'colleague@example.com',
                    value: inviteEmail,
                    oninput: (e) => { inviteEmail = e.target.value; inviteError = null; inviteSuccess = null },
                    onkeydown: (e) => { if (e.key === 'Enter') inviteMember(projectId) },
                  }),
                ]),
                m('div', [
                  m('label', { class: labelStyle }, 'Role'),
                  m('select', {
                    class: inviteSelect,
                    value: inviteRole,
                    onchange: (e) => { inviteRole = e.target.value },
                  }, [
                    m('option', { value: 'editor' }, 'Editor'),
                    m('option', { value: 'viewer' }, 'Viewer'),
                  ]),
                ]),
                m('button', {
                  class: inviteBtn,
                  disabled: inviting || !inviteEmail.trim(),
                  onclick: () => inviteMember(projectId),
                }, inviting ? 'Inviting...' : 'Invite'),
              ])
            : null,

          inviteError ? m('div', { class: errMsg }, inviteError) : null,
          inviteSuccess ? m('div', { class: successMsg }, inviteSuccess) : null,
        ]),

        // Invite links section (owner only)
        isOwner
          ? m('div', { class: section }, (() => {
              const children = []
              children.push(m('h2', { class: sectionTitle }, 'Invite Links'))

              // Active links
              const activeLinks = inviteLinks.filter(l => !l.revoked_at)
              const revokedLinks = inviteLinks.filter(l => l.revoked_at)

              if (activeLinks.length > 0 || revokedLinks.length > 0) {
                children.push(m('div', { class: card },
                  activeLinks.map(link =>
                    m('div', { key: link.id, class: inviteLinkRow }, [
                      m('div', { class: inviteLinkInfo }, [
                        m('div', { class: inviteLinkLabel }, link.label || 'Invite link'),
                        m('div', { class: inviteLinkCode }, link.code),
                      ]),
                      m('span', {
                        class: roleBadge,
                        style: `color: ${roleColor(link.role)}; background: ${roleColor(link.role)}15;`,
                      }, link.role),
                      m('button', {
                        class: copyBtn,
                        onclick: () => copyInviteLink(link.code),
                      }, copiedCode === link.code ? 'Copied!' : 'Copy link'),
                      m('button', {
                        class: removeBtn,
                        onclick: () => revokeInviteLink(projectId, link.id),
                      }, 'Revoke'),
                    ])
                  ).concat(
                    revokedLinks.map(link =>
                      m('div', { key: link.id, class: `${inviteLinkRow} ${revokedStyle}` }, [
                        m('div', { class: inviteLinkInfo }, [
                          m('div', { class: inviteLinkLabel }, link.label || 'Invite link'),
                          m('div', { class: inviteLinkCode }, link.code),
                        ]),
                        m('span', {
                          class: roleBadge,
                          style: `color: ${colors.textMuted}; background: ${colors.textMuted}15;`,
                        }, 'revoked'),
                      ])
                    )
                  )
                ))
              } else if (!inviteLinksLoading) {
                children.push(m('p', {
                  style: `font-size: ${font.size.sm}; color: ${colors.textMuted};`,
                }, 'No invite links yet. Create one to share with collaborators.'))
              }

              // Create form
              children.push(m('div', { class: inviteForm }, [
                m('div', { style: 'flex:1;' }, [
                  m('label', { class: labelStyle }, 'Label (optional)'),
                  m('input', {
                    class: inviteInput,
                    type: 'text',
                    placeholder: 'e.g. For QA team',
                    value: newLinkLabel,
                    oninput: (e) => { newLinkLabel = e.target.value },
                  }),
                ]),
                m('div', [
                  m('label', { class: labelStyle }, 'Role'),
                  m('select', {
                    class: inviteSelect,
                    value: newLinkRole,
                    onchange: (e) => { newLinkRole = e.target.value },
                  }, [
                    m('option', { value: 'editor' }, 'Editor'),
                    m('option', { value: 'viewer' }, 'Viewer'),
                  ]),
                ]),
                m('button', {
                  class: inviteBtn,
                  disabled: creatingLink,
                  onclick: () => createInviteLink(projectId),
                }, creatingLink ? 'Creating...' : 'Create link'),
              ]))

              return children
            })())
          : null,

        // Scenario generation LLM config (owner only)
        isOwner
          ? m('div', { class: section }, [
              m('h2', {
                class: sectionTitle,
                style: 'cursor: pointer;',
                onclick: () => { showGenConfig = !showGenConfig },
              }, [
                'Scenario Generation LLM',
                m('span', { style: `font-size: ${font.size.sm}; color: ${colors.textMuted}; margin-left: ${space.sm};` },
                  showGenConfig ? '(collapse)' : '(expand)'),
              ]),
              m('p', { style: `font-size: ${font.size.sm}; color: ${colors.textMuted}; margin-bottom: ${space.md};` },
                'Configure the LLM used for AI-generated scenarios.'),
              showGenConfig
                ? m('div', { class: `${card} ${configForm}`, style: `padding: ${space.lg};` }, [
                    m('div', { class: configRow }, [
                      m('span', { class: configLabel }, 'Provider'),
                      m('select', {
                        class: configInput,
                        style: 'max-width: 200px;',
                        value: genProvider,
                        onchange: (e) => { genProvider = e.target.value },
                      }, [
                        m('option', { value: 'openai' }, 'OpenAI'),
                        m('option', { value: 'google' }, 'Google Gemini'),
                        m('option', { value: 'anthropic' }, 'Anthropic'),
                        m('option', { value: 'custom' }, 'Custom / Proxy'),
                      ]),
                    ]),
                    m('div', { class: configRow }, [
                      m('span', { class: configLabel }, 'Model'),
                      m('input', {
                        class: configInput,
                        value: genModel,
                        oninput: (e) => { genModel = e.target.value },
                      }),
                    ]),
                    (genProvider === 'custom' || genProvider === 'google')
                      ? m('div', { class: configRow }, [
                          m('span', { class: configLabel }, 'Base URL'),
                          m('input', {
                            class: configInput,
                            style: 'max-width: 400px;',
                            placeholder: 'https://your-proxy.com/v1',
                            value: genBaseUrl,
                            oninput: (e) => { genBaseUrl = e.target.value },
                          }),
                        ])
                      : null,
                    m('div', { class: configRow }, [
                      m('span', { class: configLabel }, 'API Key'),
                      m('input', {
                        class: configInput,
                        type: 'password',
                        placeholder: genConfig?.hasApiKey ? '(key saved)' : 'Enter API key',
                        value: genApiKey,
                        oninput: (e) => { genApiKey = e.target.value },
                      }),
                    ]),
                    m('div', { style: `display: flex; align-items: center; gap: ${space.sm}; margin-top: ${space.sm};` }, [
                      m('button', {
                        class: btnPrimary,
                        disabled: genSaving || !genProvider.trim() || !genModel.trim(),
                        onclick: () => saveGenConfig(projectId),
                      }, genSaving ? 'Saving...' : 'Save'),
                      genSaveMsg ? m('span', { style: `font-size: ${font.size.sm}; color: ${genSaveMsg === 'Saved' ? colors.teal : colors.critical};` }, genSaveMsg) : null,
                    ]),
                  ])
                : null,
            ])
          : null,

        // Tag predictions config (owner only)
        isOwner
          ? m('div', { class: section }, [
              m('h2', {
                class: sectionTitle,
                style: 'cursor: pointer;',
                onclick: () => { showPredConfig = !showPredConfig },
              }, [
                'Tag Predictions',
                m('span', { style: `font-size: ${font.size.sm}; color: ${colors.textMuted}; margin-left: ${space.sm};` },
                  showPredConfig ? '(collapse)' : '(expand)'),
              ]),
              m('p', { style: `font-size: ${font.size.sm}; color: ${colors.textMuted}; margin-bottom: ${space.md};` },
                'Configure the LLM used for AI-powered tag predictions.'),
              showPredConfig
                ? m('div', { class: `${card} ${configForm}`, style: `padding: ${space.lg};` }, [
                    m('div', { class: configRow }, [
                      m('span', { class: configLabel }, 'Provider'),
                      m('select', {
                        class: configInput,
                        style: 'max-width: 200px;',
                        value: predProvider,
                        onchange: (e) => { predProvider = e.target.value },
                      }, [
                        m('option', { value: 'openai' }, 'OpenAI'),
                        m('option', { value: 'google' }, 'Google Gemini'),
                        m('option', { value: 'anthropic' }, 'Anthropic'),
                        m('option', { value: 'custom' }, 'Custom / Proxy'),
                      ]),
                    ]),
                    m('div', { class: configRow }, [
                      m('span', { class: configLabel }, 'Model'),
                      m('input', {
                        class: configInput,
                        value: predModel,
                        oninput: (e) => { predModel = e.target.value },
                      }),
                    ]),
                    (predProvider === 'custom' || predProvider === 'google')
                      ? m('div', { class: configRow }, [
                          m('span', { class: configLabel }, 'Base URL'),
                          m('input', {
                            class: configInput,
                            style: 'max-width: 400px;',
                            placeholder: 'https://your-proxy.com/v1',
                            value: predBaseUrl,
                            oninput: (e) => { predBaseUrl = e.target.value },
                          }),
                        ])
                      : null,
                    m('div', { class: configRow }, [
                      m('span', { class: configLabel }, 'API Key'),
                      m('input', {
                        class: configInput,
                        type: 'password',
                        placeholder: predConfig?.hasApiKey ? '(key saved)' : 'Enter API key',
                        value: predApiKey,
                        oninput: (e) => { predApiKey = e.target.value },
                      }),
                    ]),
                    m('div', { class: configRow }, [
                      m('span', { class: configLabel }, 'Granularity'),
                      m('select', {
                        class: configInput,
                        style: 'max-width: 200px;',
                        value: predGranularity,
                        onchange: (e) => { predGranularity = e.target.value },
                      }, [
                        m('option', { value: 'session' }, 'Per session'),
                        m('option', { value: 'message' }, 'Per message'),
                        m('option', { value: 'author' }, 'Per author role'),
                      ]),
                    ]),
                    m('div', { class: configRow }, [
                      m('span', { class: configLabel }, 'Concurrency'),
                      m('input', {
                        class: configInput,
                        style: 'max-width: 80px;',
                        type: 'number',
                        min: 1,
                        max: 20,
                        value: predConcurrency,
                        oninput: (e) => { predConcurrency = Math.max(1, Math.min(20, parseInt(e.target.value) || 1)) },
                      }),
                      m('span', { style: `font-size: ${font.size.xs}; color: ${colors.textMuted};` }, 'parallel API calls'),
                    ]),
                    m('div', { class: configRow }, [
                      m('span', { class: configLabel }, 'Max retries'),
                      m('input', {
                        class: configInput,
                        style: 'max-width: 80px;',
                        type: 'number',
                        min: 0,
                        max: 10,
                        value: predMaxRetries,
                        oninput: (e) => { predMaxRetries = parseInt(e.target.value) || 0 },
                      }),
                    ]),
                    m('div', [
                      m('span', { class: configLabel, style: 'display: block; margin-bottom: 4px;' }, 'Prompt template'),
                      m('textarea', {
                        class: promptTextarea,
                        placeholder: 'Leave blank for default. Use {{tags}} and {{content}} placeholders.',
                        value: predPromptTemplate,
                        oninput: (e) => { predPromptTemplate = e.target.value },
                      }),
                      m('div', { class: helpText }, 'Use {{tags}} for the tag list and {{content}} for the session content.'),
                    ]),
                    m('div', { style: 'display: flex; gap: 8px; align-items: center;' }, [
                      m('button', {
                        class: btnPrimary,
                        disabled: predSaving,
                        onclick: () => savePredictionConfig(projectId),
                      }, predSaving ? 'Saving...' : 'Save Configuration'),
                      predSaveMsg
                        ? m('span', {
                            style: `font-size: ${font.size.sm}; color: ${predSaveMsg === 'Saved' ? colors.green : colors.critical};`,
                          }, predSaveMsg)
                        : null,
                    ]),
                  ])
                : null,
            ])
          : null,

        // Danger zone (owner only)
        isOwner
          ? m('div', { class: dangerZone }, [
              m('h2', { class: dangerTitle }, 'Danger zone'),
              m('p', { class: dangerDesc },
                'Deleting this project will permanently remove all sessions, uploads, tags, rubrics, evaluations, and member data. This action cannot be undone.'),
              m('button', {
                class: dangerBtn,
                onclick: () => { showDeleteConfirm = true; deleteConfirmName = '' },
              }, 'Delete this project'),
            ])
          : null,

        // Delete confirmation dialog
        showDeleteConfirm
          ? m('div', {
              class: confirmOverlay,
              onclick: (e) => { if (e.target === e.currentTarget) { showDeleteConfirm = false } },
            }, m('div', { class: confirmCard }, [
              m('h3', { style: `margin-bottom: ${space.md};` }, 'Delete project'),
              m('p', { style: `font-size: ${font.size.sm}; color: ${colors.textSecondary}; margin-bottom: ${space.md}; line-height: 1.5;` },
                ['Type ', m('strong', project.name), ' to confirm deletion.']),
              m('input', {
                class: css`${inputStyle}; width: 100%; margin-bottom: ${space.md};`,
                type: 'text',
                placeholder: project.name,
                value: deleteConfirmName,
                oninput: (e) => { deleteConfirmName = e.target.value
                   },
                onkeydown: (e) => {
                  if (e.key === 'Enter' && deleteConfirmName === project.name) deleteProject(projectId)
                  if (e.key === 'Escape') showDeleteConfirm = false
                },
              }),
              deleteError
                ? m('div', { style: `color: ${colors.critical}; font-size: ${font.size.sm}; margin-bottom: ${space.md};` }, deleteError)
                : null,
              m('div', { style: 'display: flex; gap: 8px; justify-content: flex-end;' }, [
                m('button', {
                  class: btnSecondary,
                  onclick: () => { showDeleteConfirm = false },
                }, 'Cancel'),
                m('button', {
                  class: dangerBtn,
                  disabled: deleteConfirmName !== project.name || deleting,
                  onclick: () => deleteProject(projectId),
                }, deleting ? 'Deleting...' : 'Delete permanently'),
              ]),
            ]))
          : null,
      ])
    },
  }
}
